import {
  Box,
  Modal,
  Pagination,
  useMediaQuery,
  LinearProgress,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { RiDeleteBin6Fill, RiEdit2Fill, RiArticleLine } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import {
  createGame,
  updateGame,
  deleteGame,
  getAllActiveGamesWithPagination,
} from "../Redux/Slice/game.slice";
import * as Yup from "yup";
import { useFormik } from "formik";
import { getAllCategories } from "../Redux/Slice/category.slice";
import { getAllBlogs } from "../Redux/Slice/blog.slice";
import {
  FaApple,
  FaCheck,
  FaChevronLeft,
  FaChevronRight,
  FaWindows,
  FaSearch,
  FaPlus,
  FaTimes,
  FaDownload,
  FaVideo,
  FaGamepad,
} from "react-icons/fa";
import { DiAndroid } from "react-icons/di";
import { useSnackbar } from "notistack";
import axios from "axios";
import { BASE_URL } from "../Utils/baseUrl";
import { motion, AnimatePresence } from "framer-motion";

const UploadProgressToast = ({ broadcaster }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const removeListener = broadcaster.subscribe((p) => {
      setProgress(p);
    });
    return removeListener;
  }, [broadcaster]);

  return (
    <Box sx={{ width: "100%", minWidth: "250px" }}>
      <Typography
        variant="body2"
        sx={{ mb: 1, color: "white", fontWeight: "bold" }}
      >
        {progress < 100 ? `Uploading... ${progress}%` : "Processing Content..."}
      </Typography>
      <LinearProgress
        variant={progress < 100 ? "determinate" : "indeterminate"}
        value={progress}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: "rgba(255,255,255,0.1)",
          "& .MuiLinearProgress-bar": {
            backgroundColor: progress < 100 ? "#6263ED" : "#34D399",
            boxShadow:
              progress < 100 ? "0 0 10px rgba(98, 99, 237, 0.5)" : "none",
          },
        }}
      />
    </Box>
  );
};

class ProgressBroadcaster {
  constructor() {
    this.listeners = [];
  }
  subscribe(fn) {
    this.listeners.push(fn);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== fn);
    };
  }
  emit(val) {
    this.listeners.forEach((l) => l(val));
  }
}

const CustomCheckbox = ({ checked, onChange, label, className = "" }) => {
  return (
    <div
      onClick={() => onChange(!checked)}
      className={`flex items-center cursor-pointer select-none group p-3 rounded-xl transition-colors ${checked ? "bg-primary/10 border border-primary/20" : "bg-white/5 border border-white/10"} ${className}`}
    >
      <div
        className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all duration-200 
        ${
          checked
            ? "bg-primary border-primary shadow-[0_0_10px_#6263ED]"
            : "bg-transparent border-gray-600 group-hover:border-primary"
        }`}
      >
        {checked && <FaCheck className="text-white text-[10px]" />}
      </div>
      {label && (
        <span
          className={`ml-3 font-bold transition-colors ${checked ? "text-white" : "text-gray-400 group-hover:text-white"}`}
        >
          {label}
        </span>
      )}
    </div>
  );
};

export default function Game() {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Blog State
  const [blogOpen, setBlogOpen] = useState(false);
  const [blogData, setBlogData] = useState({
    title: "",
    scheduleTime: "",
    gameId: "",
    images: [],
  });
  const [loadingBlog, setLoadingBlog] = useState(false);

  const handleBlogOpen = (game) => {
    const allImages = [];
    if (game.cover_image?.url) allImages.push(game.cover_image.url);
    if (game.images && Array.isArray(game.images)) {
      game.images.forEach((img) => {
        if (typeof img === "string") allImages.push(img);
        else if (img.url) allImages.push(img.url);
      });
    }

    setBlogData({
      title: game.title,
      scheduleTime: "",
      gameId: game._id,
      images: allImages,
    });
    setBlogOpen(true);
  };

  const handleBlogClose = () => {
    setBlogOpen(false);
    setBlogData({ title: "", scheduleTime: "", gameId: "", images: [] });
  };

  const handleScheduleBlog = async (e) => {
    e.preventDefault();
    if (!blogData.scheduleTime) {
      enqueueSnackbar("Please select a schedule time", { variant: "warning" });
      return;
    }

    setLoadingBlog(true);
    try {
      const { data } = await axios.post(
        `${BASE_URL}/createScheduledBlog`,
        blogData,
        { withCredentials: true },
      );
      if (data.success) {
        enqueueSnackbar(data.message, { variant: "success" });
        handleBlogClose();
      } else {
        enqueueSnackbar(data.message, { variant: "error" });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar(
        error.response?.data?.message || "Error scheduling blog",
        { variant: "error" },
      );
    } finally {
      setLoadingBlog(false);
    }
  };

  const [gameData, setGameData] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const fileInputRef = useRef(null);
  const isSmallScreen = useMediaQuery("(max-width:425px)");
  const categories = useSelector((state) => state.category.categories);
  const allBlogs = useSelector((state) => state.blog.blogs);

  const [instructionInput, setInstructionInput] = useState("");
  const [tagInputValue, setTagInputValue] = useState("");
  const [videoPreviewUrl, setVideoPreviewUrl] = useState("");
  const videoRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategoryQ, setSelectedCategoryQ] = useState("all");
  const gamesPerPage = 10;

  const {
    paginatedGames,
    totalGames,
    totalPages,
    loadingStates,
    error: gamesError,
  } = useSelector((state) => state.game);

  const loading = loadingStates?.createGame || loadingStates?.updateGame;

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(
        getAllActiveGamesWithPagination({
          page: currentPage,
          limit: gamesPerPage,
          category: selectedCategoryQ,
          search: searchValue?.trim()?.toLowerCase(),
          userside: "false",
        }),
      );
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [currentPage, selectedCategoryQ, searchValue, dispatch]);

  useEffect(() => {
    dispatch(getAllCategories({ userside: "false" }));
    dispatch(getAllBlogs());
  }, []);

  // Validation schema
  const validationSchema = Yup.object({
    title: Yup.string().required("Game title is required"),
    description: Yup.string().required("Description is required"),
    category: Yup.string().required("Category is required"),
    cover_image: Yup.mixed()
      .required("Cover image is required")
      .test("fileFormat", "Unsupported Format", (value) => {
        if (!value) return false;
        if (typeof value === "string") return true;
        return [
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
          "image/jpg",
          "image/bmp",
          "image/svg+xml",
          "image/tiff",
          "image/x-icon",
          "image/jfif",
          "image/heic",
          "image/heif",
          "image/avif",
          "image/jp2",
        ].includes(value.type);
      }),
    video: Yup.mixed()
      .required("Video is required")
      .test("fileFormat", "Unsupported Format", (value) => {
        if (!value) return false;
        if (typeof value === "string") return true;
        return [
          "video/mp4",
          "video/webm",
          "video/ogg",
          "video/quicktime",
          "video/x-msvideo",
          "video/x-matroska",
          "video/mpeg",
          "video/x-flv",
          "video/3gpp",
          "video/3gpp2",
          "video/x-ms-wmv",
          "video/x-ms-asf",
        ].includes(value.type);
      })
      .test(
        "minDuration",
        "Video must be at least 2 minutes long",
        async (value) => {
          if (!value) return true;
          if (typeof value === "string") return true;

          return new Promise((resolve) => {
            const video = document.createElement("video");
            video.preload = "metadata";
            video.onloadedmetadata = () => {
              URL.revokeObjectURL(video.src);
              resolve(video.duration <= 120);
            };
            video.onerror = () => {
              URL.revokeObjectURL(video.src);
              resolve(false);
            };
            video.src = URL.createObjectURL(value);
          });
        },
      ),
    instructions: Yup.array()
      .ensure()
      .compact((v) => !v || !v.trim())
      .of(
        Yup.string()
          .trim()
          .min(2, "Each feature must be at least 2 characters"),
      )
      .min(1, "Add at least Two key feature"),
    isActive: Yup.boolean(),
    tags: Yup.array()
      .of(Yup.string().trim().min(1, "Tag cannot be empty"))
      .min(1, "At least one tag is required"),
    images: Yup.array()
      .of(
        Yup.mixed().test("fileFormat", "Unsupported Format", (value) => {
          if (!value) return false;
          if (typeof value === "string") return true;
          return [
            "image/jpeg",
            "image/png",
            "image/gif",
            "image/webp",
            "image/jpg",
            "image/bmp",
            "image/svg+xml",
            "image/tiff",
            "image/x-icon",
            "image/jfif",
            "image/heic",
            "image/heif",
            "image/avif",
            "image/jp2",
          ].includes(value.type);
        }),
      )
      .min(1, "At least one image is required"),
    windows_file: Yup.mixed()
      .nullable()
      .test(
        "windows-file-required",
        "Windows file is required when Windows platform is available",
        function (value) {
          const { platforms } = this.parent;
          const windows = platforms?.windows;
          if (!windows?.available) return true;
          const hasExisting = !!(
            windows?.download_link && String(windows.download_link).trim()
          );
          const hasFile = !!value && value !== "";
          return hasExisting || hasFile;
        },
      ),
    ios_file: Yup.mixed()
      .nullable()
      .test(
        "ios-file-required",
        "iOS file is required when iOS platform is available",
        function (value) {
          const { platforms } = this.parent || {};
          const ios = platforms?.ios;
          if (!ios?.available) return true;
          const hasExisting = !!(
            ios?.download_link && String(ios.download_link).trim()
          );
          const hasFile = !!value && value !== "";
          return hasExisting || hasFile;
        },
      ),
    android_file: Yup.mixed()
      .nullable()
      .test(
        "android-file-required",
        "Android file is required when Android platform is available",
        function (value) {
          const android = this.parent?.platforms?.android;
          if (!android?.available) return true;
          const hasExisting = !!(
            android?.download_link && String(android.download_link).trim()
          );
          const hasFile = !!value && value !== "";
          return hasExisting || hasFile;
        },
      ),
    platforms: Yup.object()
      .test(
        "at-least-one-platform",
        "At least one platform must be selected",
        function (value) {
          return (
            value?.windows?.available ||
            value?.ios?.available ||
            value?.android?.available
          );
        },
      )
      .shape({
        windows: Yup.object().shape({
          available: Yup.boolean(),
          price: Yup.string().when("available", {
            is: true,
            then: (schema) => schema.required("Price is required"),
            otherwise: (schema) => schema.notRequired(),
          }),
          system_requirements: Yup.object().when("available", {
            is: true,
            then: (schema) =>
              schema.shape({
                os: Yup.string().required("OS is required"),
                processor: Yup.string().required("Processor is required"),
                memory: Yup.string().required("Memory is required"),
                graphics: Yup.string().required("Graphics is required"),
                storage: Yup.string().required("Storage is required"),
              }),
            otherwise: (schema) => schema.notRequired(),
          }),
        }),
        ios: Yup.object().shape({
          available: Yup.boolean(),
          price: Yup.string().when("available", {
            is: true,
            then: (schema) => schema.required("Price is required"),
            otherwise: (schema) => schema.notRequired(),
          }),
          system_requirements: Yup.object().when("available", {
            is: true,
            then: (schema) =>
              schema.shape({
                ios_version: Yup.string().required("iOS version required"),
              }),
            otherwise: (schema) => schema.notRequired(),
          }),
        }),
        android: Yup.object().shape({
          available: Yup.boolean(),
          price: Yup.string().when("available", {
            is: true,
            then: (schema) => schema.required("Price is required"),
            otherwise: (schema) => schema.notRequired(),
          }),
          system_requirements: Yup.object().when("available", {
            is: true,
            then: (schema) =>
              schema.shape({
                android_version: Yup.string().required(
                  "Android version required",
                ),
              }),
            otherwise: (schema) => schema.notRequired(),
          }),
        }),
      }),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      category: "",
      cover_image: null,
      video: null,
      instructions: [],
      isActive: true,
      tags: [],
      images: [],
      platforms: {
        windows: {
          available: false,
          price: "",
          download_link: "",
          system_requirements: {
            os: "",
            processor: "",
            memory: "",
            graphics: "",
            storage: "",
          },
        },
        ios: {
          available: false,
          price: "",
          download_link: "",
          system_requirements: { ios_version: "" },
        },
        android: {
          available: false,
          price: "",
          download_link: "",
          system_requirements: { android_version: "" },
        },
      },
      windows_file: null,
      ios_file: null,
      android_file: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      // console.log("vaa",values);
      
      const broadcaster = new ProgressBroadcaster();
      const snackbarId = enqueueSnackbar(
        <UploadProgressToast broadcaster={broadcaster} />,
        {
          variant: "default",
          persist: true,
          style: {
            minWidth: "300px",
            backgroundColor: "#141414",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "16px",
          },
        },
      );

      const onProgress = (percent) => broadcaster.emit(percent);

      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("category", values.category);
      formData.append(
        "instructions",
        JSON.stringify(values.instructions) || "",
      );
      formData.append("isActive", values.isActive ? "true" : "false");
      formData.append("tags", JSON.stringify(values.tags));
      if (values.cover_image && typeof values.cover_image !== "string")
        formData.append("cover_image", values.cover_image);
      if (values.video && typeof values.video !== "string")
        formData.append("video", values.video);
      if (values.windows_file && typeof values.windows_file !== "string")
        formData.append("windows_file", values.windows_file);
      if (values.ios_file && typeof values.ios_file !== "string")
        formData.append("ios_file", values.ios_file);
      if (values.android_file && typeof values.android_file !== "string")
        formData.append("android_file", values.android_file);
      if (values.images && Array.isArray(values.images)) {
        values.images.forEach((img) => formData.append("images", img));
      }
      formData.append("platforms", JSON.stringify(values.platforms));

      try {
        if (gameData)
          await dispatch(
            updateGame({ _id: gameData._id, formData, onProgress }),
          );
        else await dispatch(createGame({ formData, onProgress }));
      } catch (error) {
        console.error("Error submitting game:", error);
      } finally {
        closeSnackbar(snackbarId);
        handleCreateClose();
        formik.resetForm();
      }
    },
  });

  useEffect(() => {
    let objectUrl;
    if (formik.values.video && typeof formik.values.video !== "string") {
      objectUrl = URL.createObjectURL(formik.values.video);
      setVideoPreviewUrl(objectUrl);
    } else if (typeof formik.values.video === "string") {
      setVideoPreviewUrl(formik.values.video);
    } else {
      setVideoPreviewUrl("");
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [formik.values.video]);

  const normalizeTagsInput = (rawValue) => {
    if (!rawValue || typeof rawValue !== "string") return "";
    const trimmed = rawValue.trim();
    const endsWithSeparator = /[^a-zA-Z0-9]$/.test(trimmed);
    let cleaned = trimmed
      .replace(/['"“”‘’]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, ",")
      .replace(/\,+/g, ",")
      .replace(/^,|,+$/g, "");

    if (endsWithSeparator && cleaned) {
      cleaned += ", ";
    }

    return cleaned;
  };

  const parseInstructionInput = (rawValue) => {
    if (!rawValue || typeof rawValue !== "string") return [];
    const normalized = rawValue.replace(/[\u0000-\u001F\u007F]+/g, " ").trim();
    const extracted = [];
    const quoteRegex = /"([^"]+)"|'([^']+)'/g;
    let match;

    while ((match = quoteRegex.exec(normalized)) !== null) {
      const phrase = (match[1] || match[2] || "").trim().replace(/['"]/g, "");
      if (phrase) extracted.push(phrase);
    }

    if (extracted.length > 0) return extracted;

    return normalized
      .split(/,|\n|\r/)
      .map((item) => item.trim().replace(/['"]/g, ""))
      .filter(Boolean);
  };

  const addInstruction = () => {
    const entries = parseInstructionInput(instructionInput);
    if (!entries.length) return;
    formik.setFieldValue("instructions", [
      ...formik.values.instructions,
      ...entries,
    ]);
    setInstructionInput("");
  };

  const removeInstruction = (index) => {
    const updated = [...formik.values.instructions];
    updated.splice(index, 1);
    formik.setFieldValue("instructions", updated);
  };

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleOpen = (data) => {
    setCreateOpen(true);
    setGameData(data);
    if (data) {
      formik.setValues({
        title: data.title,
        description: data.description,
        category: data.category?._id || "",
        cover_image: data.cover_image?.url || null,
        video: data.video?.url || null,
        instructions: data.instructions || [],
        isActive: data.isActive || true,
        tags: data.tags || [],
        images: Array.isArray(data.images)
          ? data.images.map((img) => (typeof img === "string" ? img : img.url))
          : [],
        windows_file: data.platforms?.windows?.download_link,
        ios_file: data.platforms?.ios?.download_link,
        android_file: data.platforms?.android?.download_link,
        platforms: {
          windows: {
            available: data.platforms?.windows?.available || false,
            price: data.platforms?.windows?.price || "",
            download_link: data.platforms?.windows?.download_link || "",
            system_requirements: {
              os: data.platforms?.windows?.system_requirements?.os || "",
              processor:
                data.platforms?.windows?.system_requirements?.processor || "",
              memory:
                data.platforms?.windows?.system_requirements?.memory || "",
              graphics:
                data.platforms?.windows?.system_requirements?.graphics || "",
              storage:
                data.platforms?.windows?.system_requirements?.storage || "",
            },
          },
          ios: {
            available: data.platforms?.ios?.available || false,
            price: data.platforms?.ios?.price || "",
            download_link: data.platforms?.ios?.download_link || "",
            system_requirements: {
              ios_version:
                data.platforms?.ios?.system_requirements?.ios_version || "",
            },
          },
          android: {
            available: data.platforms?.android?.available || false,
            price: data.platforms?.android?.price || "",
            download_link: data.platforms?.android?.download_link || "",
            system_requirements: {
              android_version:
                data.platforms?.android?.system_requirements?.android_version ||
                "",
            },
          },
        },
      });
      setTagInputValue(
        normalizeTagsInput(
          Array.isArray(data.tags) ? data.tags.join(", ") : "",
        ),
      );
    }
  };

  const handleCreateClose = () => {
    setCreateOpen(false);
    setGameData("");
    setTagInputValue("");
    formik.resetForm();
  };

  const handleExportExcel = async () => {
    try {
      enqueueSnackbar("Generating Excel file...", { variant: "info" });
      const response = await axios.get(`${BASE_URL}/exportGamesToExcel`, {
        responseType: "blob",
        withCredentials: true,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `games_export_${new Date().getTime()}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      enqueueSnackbar("Excel file downloaded successfully!", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Failed to export Excel", { variant: "error" });
    }
  };

  const handleDeleteClose = () => {
    setDelOpen(false);
    setGameData("");
  };

  const handleDeleteGame = () => {
    dispatch(deleteGame(gameData._id));
    handleDeleteClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">
            Games
          </h1>
          <p className="text-gray-400">Manage all games in the library.</p>
        </div>
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleExportExcel}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-white/5 text-white font-bold border border-white/10 hover:bg-white/10 transition-all"
          >
            <FaDownload size={14} /> Export
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setCreateOpen(true)}
            className="flex items-center justify-center gap-2 px-6 py-3 rounded-2xl bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 transition-all"
          >
            <FaPlus /> Add Game
          </motion.button>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm">
        <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row gap-4 items-center justify-between">
          <div className="relative w-full sm:w-80 group">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 group-focus-within:text-primary transition-colors" />
            <input
              type="text"
              placeholder="Search by title, tag, or category..."
              value={searchValue}
              onChange={(e) => {
                setCurrentPage(1);
                setSearchValue(e.target.value);
              }}
              className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-2.5 text-sm text-white focus:border-primary/50 outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-4">
            <select
              value={selectedCategoryQ}
              onChange={(e) => {
                setCurrentPage(1);
                setSelectedCategoryQ(e.target.value);
              }}
              className="bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-xs text-gray-400 focus:text-white outline-none"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.categoryName}
                </option>
              ))}
            </select>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest">
              Total Games: {totalGames}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-white/[0.02]">
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">
                  Game Details
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">
                  Category & Tags
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">
                  Platforms
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-center">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-bold text-gray-500 uppercase tracking-widest text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {paginatedGames.map((game, index) => (
                <tr
                  key={index}
                  className="hover:bg-white/[0.02] transition-colors group"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={game.cover_image?.url}
                        alt=""
                        className="w-14 h-14 rounded-xl object-cover border border-white/10"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-white group-hover:text-primary transition-colors">
                          {game.title}
                        </span>
                        <span className="text-[10px] text-gray-500 mt-1 uppercase tracking-wider">
                          {new Date(game.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1.5">
                      <span className="text-xs text-primary font-bold">
                        {game.category?.categoryName}
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {game.tags?.slice(0, 3).map((tag, i) => (
                          <span
                            key={i}
                            className="text-[9px] bg-white/5 border border-white/5 px-2 py-0.5 rounded text-gray-400"
                          >
                            #{tag.replace(/"/g, "")}
                          </span>
                        ))}
                        {game.tags?.length > 3 && (
                          <span className="text-[9px] text-gray-500">
                            +{game.tags.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-3">
                      {game.platforms?.windows?.available && (
                        <FaWindows className="text-blue-400" title="Windows" />
                      )}
                      {game.platforms?.ios?.available && (
                        <FaApple className="text-gray-300" title="iOS" />
                      )}
                      {game.platforms?.android?.available && (
                        <DiAndroid className="text-green-500" title="Android" />
                      )}
                      {!game.platforms?.windows?.available &&
                        !game.platforms?.ios?.available &&
                        !game.platforms?.android?.available && (
                          <span className="text-xs text-gray-600">N/A</span>
                        )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span
                      className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${game.isActive ? "bg-green-500/10 text-green-400 border border-green-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}
                    >
                      {game.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      {!allBlogs?.some(
                        (b) =>
                          b.title?.toLowerCase() === game.title?.toLowerCase(),
                      ) && (
                        <button
                          onClick={() => handleBlogOpen(game)}
                          className="p-2 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500 hover:text-white transition-all border border-blue-500/20"
                          title="Schedule Blog"
                        >
                          <RiArticleLine size={14} />
                        </button>
                      )}
                      <button
                        onClick={() => handleOpen(game)}
                        className="p-2 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500 hover:text-white transition-all border border-green-500/20"
                      >
                        <RiEdit2Fill size={14} />
                      </button>
                      <button
                        onClick={() => {
                          setGameData(game);
                          setDelOpen(true);
                        }}
                        className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white transition-all border border-red-500/20"
                      >
                        <RiDeleteBin6Fill size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="p-6 border-t border-white/5 flex justify-end">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => handlePageChange(page)}
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#9ca3af",
                  borderColor: "rgba(255,255,255,0.1)",
                  borderRadius: "12px",
                },
                "& .Mui-selected": {
                  backgroundColor: "rgba(98,99,237,0.2) !important",
                  color: "#6263ED !important",
                  borderColor: "rgba(98,99,237,0.5) !important",
                },
              }}
            />
          </div>
        )}
      </div>

      {/* Main Game Modal */}
      <Modal
        open={createOpen}
        onClose={handleCreateClose}
        className="flex items-center justify-center p-5"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#141414] border border-white/10 rounded-xl w-full max-w-2xl shadow-2xl outline-none overflow-hidden max-h-[88vh] flex flex-col"
        >
          <div className="p-6 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#141414]/95 backdrop-blur-xl z-10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <FaGamepad size={16} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  {gameData ? "Edit Game" : "Add New Game"}
                </h2>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">
                  Game Settings
                </p>
              </div>
            </div>
            <button
              onClick={handleCreateClose}
              className="p-2 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-all border border-white/10"
            >
              <FaTimes size={16} />
            </button>
          </div>

          <form
            onSubmit={formik.handleSubmit}
            className="overflow-y-auto p-6 space-y-4 no-scrollbar"
          >
            {/* Section 1: Identity */}
            <section className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-primary font-bold text-sm">01</span>
                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">
                  Basic Information
                </h3>
                <div className="flex-1 h-px bg-white/5" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                    Game Title
                  </label>
                  <input
                    name="title"
                    type="text"
                    placeholder="Cyberpunk 2077"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-primary/50 focus:ring-1 focus:ring-primary/50 outline-none transition-all font-medium"
                    {...formik.getFieldProps("title")}
                  />
                  {formik.touched.title && formik.errors.title && (
                    <p className="text-red-400 text-[9px] ml-1">
                      {formik.errors.title}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                    Category
                  </label>
                  <select
                    name="category"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-primary/50 outline-none transition-all font-medium appearance-none"
                    {...formik.getFieldProps("category")}
                  >
                    <option value="" className="bg-[#141414]">
                      Select Category
                    </option>
                    {categories.map((cat) => (
                      <option
                        key={cat._id}
                        value={cat._id}
                        className="bg-[#141414]"
                      >
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                  {formik.touched.category && formik.errors.category && (
                    <p className="text-red-400 text-[9px] ml-1">
                      {formik.errors.category}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                  Description
                </label>
                <textarea
                  name="description"
                  rows="3"
                  placeholder="Enter game description..."
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-primary/50 outline-none transition-all resize-none"
                  {...formik.getFieldProps("description")}
                />
                {formik.touched.description && formik.errors.description && (
                  <p className="text-red-400 text-[9px] ml-1">
                    {formik.errors.description}
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                  Tags (Comma separated)
                </label>
                <input
                  type="text"
                  placeholder="Action, Open World, RPG"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-primary/50 outline-none transition-all"
                  value={tagInputValue}
                  onChange={(e) => {
                    const cleaned = normalizeTagsInput(e.target.value);
                    setTagInputValue(cleaned);
                    formik.setFieldValue(
                      "tags",
                      cleaned
                        ? cleaned
                            .split(",")
                            .map((t) => t.trim())
                            .filter((t) => t !== "")
                        : [],
                    );
                  }}
                />
                {formik.touched.tags && formik.errors.tags && (
                  <p className="text-red-400 text-[9px] ml-1">
                    {formik.errors.tags}
                  </p>
                )}
              </div>
            </section>

            {/* Section 2: Visual Assets */}
            <section className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-primary font-bold text-sm">02</span>
                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">
                  Images & Videos
                </h3>
                <div className="flex-1 h-px bg-white/5" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {/* Cover Image */}
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                    Cover Image (16:9 recommended)
                  </label>
                  <div className="relative group h-24 rounded-lg overflow-hidden border-2 border-dashed border-white/10 bg-white/5 flex flex-col items-center justify-center transition-all hover:border-primary/50">
                    {formik.values.cover_image ? (
                      <div className="relative w-full h-full">
                        <img
                          src={
                            typeof formik.values.cover_image === "string"
                              ? formik.values.cover_image
                              : URL.createObjectURL(formik.values.cover_image)
                          }
                          className="w-full h-full object-cover"
                          alt=""
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm">
                          <span className="bg-white/20 px-4 py-1 rounded-lg text-[9px] font-bold border border-white/10">
                            Change Image
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mx-auto mb-1">
                          <FaPlus size={12} />
                        </div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-gray-500">
                          Upload Image
                        </p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) =>
                        formik.setFieldValue("cover_image", e.target.files[0])
                      }
                    />
                  </div>
                  {formik.touched.cover_image && formik.errors.cover_image && (
                    <p className="text-red-400 text-[9px]">
                      {formik.errors.cover_image}
                    </p>
                  )}
                </div>

                {/* Trailer Video */}
                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                    Game Trailer (Max 2min)
                  </label>
                  <div
                    className="relative group h-24 rounded-lg overflow-hidden border-2 border-dashed border-white/10 bg-white/5 flex flex-col items-center justify-center transition-all hover:border-primary/50"
                    onMouseEnter={() => videoRef.current?.play()}
                    onMouseLeave={() => {
                      if (videoRef.current) {
                        videoRef.current.pause();
                        videoRef.current.currentTime = 0;
                      }
                    }}
                  >
                    {formik.values.video ? (
                      <div className="relative w-full h-full">
                        <video
                          ref={videoRef}
                          src={videoPreviewUrl}
                          className="w-full h-full object-cover"
                          muted
                          loop
                          preload="metadata"
                        />
                        <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
                          <FaVideo className="text-white" size={24} />
                        </div>
                        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2">
                          <p className="text-[9px] font-bold text-white truncate">
                            {typeof formik.values.video === "string"
                              ? "Current Video"
                              : formik.values.video.name}
                          </p>
                          <button
                            type="button"
                            onClick={() => formik.setFieldValue("video", null)}
                            className="text-[8px] font-black text-red-400 uppercase tracking-widest hover:text-red-300"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center mx-auto mb-1">
                          <FaPlus size={12} />
                        </div>
                        <p className="text-[9px] font-black uppercase tracking-widest text-gray-500">
                          Upload Video
                        </p>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="video/*"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) =>
                        formik.setFieldValue("video", e.target.files[0])
                      }
                    />
                  </div>
                  {formik.touched.video && formik.errors.video && (
                    <p className="text-red-400 text-[9px]">
                      {formik.errors.video}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest ml-1">
                  Game Gallery
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {formik.values.images.map((img, i) => (
                    <div
                      key={i}
                      className="relative group aspect-square rounded-lg overflow-hidden border border-white/10 shadow-lg"
                    >
                      <img
                        src={
                          typeof img === "string"
                            ? img
                            : URL.createObjectURL(img)
                        }
                        className="w-full h-full object-cover"
                        alt=""
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = [...formik.values.images];
                          next.splice(i, 1);
                          formik.setFieldValue("images", next);
                        }}
                        className="absolute top-1 right-1 p-1 rounded-md bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-all shadow-lg scale-75 hover:scale-100"
                      >
                        <FaTimes size={8} />
                      </button>
                    </div>
                  ))}
                  <div className="relative aspect-square rounded-lg border-2 border-dashed border-white/10 bg-white/5 flex items-center justify-center group hover:border-primary/50 transition-all">
                    <FaPlus
                      size={12}
                      className="text-gray-600 group-hover:text-primary transition-colors"
                    />
                    <input
                      type="file"
                      multiple
                      accept="image/*"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) =>
                        formik.setFieldValue("images", [
                          ...formik.values.images,
                          ...Array.from(e.target.files),
                        ])
                      }
                    />
                  </div>
                </div>
                {formik.touched.images && formik.errors.images && (
                  <p className="text-red-400 text-[9px]">
                    {formik.errors.images}
                  </p>
                )}
              </div>
            </section>

            {/* Section 3: Gameplay Features */}
            <section className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-primary font-bold text-sm">03</span>
                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">
                  Game Features
                </h3>
                <div className="flex-1 h-px bg-white/5" />
              </div>

              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter feature..."
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-primary/50 outline-none transition-all"
                    value={instructionInput}
                    onChange={(e) => setInstructionInput(e.target.value)}
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), addInstruction())
                    }
                  />
                  <button
                    type="button"
                    onClick={addInstruction}
                    className="px-4 rounded-lg bg-white/10 text-white font-bold text-sm border border-white/10 hover:bg-white/20 transition-all"
                  >
                    Add
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <AnimatePresence>
                    {formik.values.instructions.map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="flex items-center justify-between gap-2 bg-white/[0.03] border border-white/5 p-2 rounded-lg"
                      >
                        <div className="flex items-center gap-2 truncate">
                          <div className="w-1 h-1 rounded-full bg-primary shadow-[0_0_8px_#6263ED]" />
                          <span className="text-xs text-gray-300 truncate">
                            {item}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeInstruction(i)}
                          className="p-1 text-gray-600 hover:text-red-400 transition-colors"
                        >
                          <FaTimes size={10} />
                        </button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
                {formik.touched.instructions && formik.errors.instructions && (
                  <p className="text-red-400 text-[9px]">
                    {formik.errors.instructions}
                  </p>
                )}
              </div>
            </section>

            {/* Section 4: Platforms & Distribution */}
            <section className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-primary font-bold text-sm">04</span>
                <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-white">
                  Platforms
                </h3>
                <div className="flex-1 h-px bg-white/5" />
              </div>

              <div className="grid grid-cols-1 gap-2">
                {/* Windows Implementation */}
                <div
                  className={`p-3 rounded-lg border transition-all ${formik.values.platforms.windows.available ? "bg-blue-500/[0.03] border-blue-500/20 shadow-lg shadow-blue-500/5" : "bg-white/5 border-white/10"}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${formik.values.platforms.windows.available ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30" : "bg-white/10 text-gray-500"}`}
                      >
                        <FaWindows size={16} />
                      </div>
                      <div>
                        <h4
                          className={`font-bold text-sm ${formik.values.platforms.windows.available ? "text-white" : "text-gray-500"}`}
                        >
                          Windows
                        </h4>
                        <p className="text-[8px] uppercase tracking-widest font-bold text-gray-500">
                          Requirements & Price
                        </p>
                      </div>
                    </div>
                    <CustomCheckbox
                      checked={formik.values.platforms.windows.available}
                      onChange={(val) =>
                        formik.setFieldValue("platforms.windows.available", val)
                      }
                    />
                  </div>

                  {formik.values.platforms.windows.available && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      className="space-y-2 overflow-hidden"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-[0.1em] text-blue-400 ml-1">
                            Price ($)
                          </label>
                          <input
                            type="number"
                            placeholder="59.99"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500/50 outline-none"
                            {...formik.getFieldProps("platforms.windows.price")}
                          />
                          {formik.touched.platforms?.windows?.price &&
                            formik.errors.platforms?.windows?.price && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {formik.errors.platforms.windows.price}
                              </p>
                            )}
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-[0.1em] text-blue-400 ml-1">
                            OS
                          </label>
                          <input
                            type="text"
                            placeholder="Windows 10/11 64-bit"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-blue-500/50 outline-none"
                            {...formik.getFieldProps(
                              "platforms.windows.system_requirements.os",
                            )}
                          />
                          {formik.touched.platforms?.windows
                            ?.system_requirements?.os &&
                            formik.errors.platforms?.windows
                              ?.system_requirements?.os && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {
                                  formik.errors.platforms.windows
                                    .system_requirements.os
                                }
                              </p>
                            )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-[0.1em] text-blue-400 ml-1">
                            Processor
                          </label>
                          <input
                            type="text"
                            placeholder="Intel Core i5-8400"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:border-blue-500/50 outline-none"
                            {...formik.getFieldProps(
                              "platforms.windows.system_requirements.processor",
                            )}
                          />
                          {formik.touched.platforms?.windows
                            ?.system_requirements?.processor &&
                            formik.errors.platforms?.windows
                              ?.system_requirements?.processor && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {
                                  formik.errors.platforms.windows
                                    .system_requirements.processor
                                }
                              </p>
                            )}
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-[0.1em] text-blue-400 ml-1">
                            Memory
                          </label>
                          <input
                            type="text"
                            placeholder="16 GB RAM"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:border-blue-500/50 outline-none"
                            {...formik.getFieldProps(
                              "platforms.windows.system_requirements.memory",
                            )}
                          />
                          {formik.touched.platforms?.windows
                            ?.system_requirements?.memory &&
                            formik.errors.platforms?.windows
                              ?.system_requirements?.memory && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {
                                  formik.errors.platforms.windows
                                    .system_requirements.memory
                                }
                              </p>
                            )}
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-[0.1em] text-blue-400 ml-1">
                            Graphics
                          </label>
                          <input
                            type="text"
                            placeholder="NVIDIA GTX 1060"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:border-blue-500/50 outline-none"
                            {...formik.getFieldProps(
                              "platforms.windows.system_requirements.graphics",
                            )}
                          />
                          {formik.touched.platforms?.windows
                            ?.system_requirements?.graphics &&
                            formik.errors.platforms?.windows
                              ?.system_requirements?.graphics && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {
                                  formik.errors.platforms.windows
                                    .system_requirements.graphics
                                }
                              </p>
                            )}
                        </div>
                        <div className="space-y-1">
                          <label className="text-[9px] font-black uppercase tracking-[0.1em] text-blue-400 ml-1">
                            Storage
                          </label>
                          <input
                            type="text"
                            placeholder="50 GB"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:border-blue-500/50 outline-none"
                            {...formik.getFieldProps(
                              "platforms.windows.system_requirements.storage",
                            )}
                          />
                          {formik.touched.platforms?.windows
                            ?.system_requirements?.storage &&
                            formik.errors.platforms?.windows
                              ?.system_requirements?.storage && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {
                                  formik.errors.platforms.windows
                                    .system_requirements.storage
                                }
                              </p>
                            )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase tracking-[0.1em] text-blue-400 ml-1">
                          Build Distribution (.exe / .zip)
                        </label>
                        <div className="relative h-12 rounded-lg bg-white/5 border-2 border-dashed border-white/10 flex items-center px-3 hover:border-blue-500/50 transition-all group">
                          <FaPlus
                            size={12}
                            className="text-gray-500 mr-2 group-hover:text-blue-500 transition-colors"
                          />
                          <div className="flex-1 overflow-hidden">
                            <p className="text-xs font-bold text-gray-400 truncate">
                              {formik.values.windows_file
                                ? typeof formik.values.windows_file === "string"
                                  ? "Current Package: " +
                                    formik.values.windows_file.split("/").pop()
                                  : formik.values.windows_file.name
                                : "Select executable package..."}
                            </p>
                          </div>
                          <input
                            type="file"
                            accept=".exe,.zip,.rar"
                            className="absolute inset-0 opacity-0 cursor-pointer"
                            onChange={(e) =>
                              formik.setFieldValue(
                                "windows_file",
                                e.target.files[0],
                              )
                            }
                          />
                        </div>
                        {formik.touched.windows_file &&
                          formik.errors.windows_file && (
                            <p className="text-red-400 text-[8px] ml-1">
                              {formik.errors.windows_file}
                            </p>
                          )}
                      </div>
                    </motion.div>
                  )}
                </div>

                {/* Mobile Deployment Container (Shared Grid) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {/* iOS Deployment */}
                  <div
                    className={`p-3 rounded-lg border transition-all ${formik.values.platforms.ios.available ? "bg-white/[0.03] border-white/20" : "bg-white/5 border-white/10"}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${formik.values.platforms.ios.available ? "bg-white text-black" : "bg-white/10 text-gray-500"}`}
                        >
                          <FaApple size={16} />
                        </div>
                        <h4
                          className={`font-bold text-sm ${formik.values.platforms.ios.available ? "text-white" : "text-gray-500"}`}
                        >
                          iOS App
                        </h4>
                      </div>
                      <CustomCheckbox
                        checked={formik.values.platforms.ios.available}
                        onChange={(val) =>
                          formik.setFieldValue("platforms.ios.available", val)
                        }
                      />
                    </div>
                    {formik.values.platforms.ios.available && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-2"
                      >
                        <div className="space-y-1">
                          <input
                            type="number"
                            placeholder="Price ($)"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-white/50 outline-none"
                            {...formik.getFieldProps("platforms.ios.price")}
                          />
                          {formik.touched.platforms?.ios?.price &&
                            formik.errors.platforms?.ios?.price && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {formik.errors.platforms.ios.price}
                              </p>
                            )}
                        </div>
                        <div className="space-y-1">
                          <input
                            type="text"
                            placeholder="Min iOS Version"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-white/50 outline-none"
                            {...formik.getFieldProps(
                              "platforms.ios.system_requirements.ios_version",
                            )}
                          />
                          {formik.touched.platforms?.ios?.system_requirements
                            ?.ios_version &&
                            formik.errors.platforms?.ios?.system_requirements
                              ?.ios_version && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {
                                  formik.errors.platforms.ios
                                    .system_requirements.ios_version
                                }
                              </p>
                            )}
                        </div>
                        <div className="space-y-1">
                          <div className="relative h-12 rounded-lg bg-white/5 border-2 border-dashed border-white/10 flex items-center px-3 hover:border-white/50 transition-all">
                            <FaPlus size={12} className="text-gray-500 mr-2" />
                            <p className="text-[9px] font-black uppercase text-gray-500 truncate">
                              {formik.values.ios_file
                                ? typeof formik.values.ios_file === "string"
                                  ? "Current Package: " +
                                    formik.values.ios_file.split("/").pop()
                                  : formik.values.ios_file.name
                                : "Upload .ipa Build"}
                            </p>
                            <input
                              type="file"
                              accept=".ipa"
                              className="absolute inset-0 opacity-0 cursor-pointer"
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "ios_file",
                                  e.target.files[0],
                                )
                              }
                            />
                          </div>
                          {formik.touched.ios_file &&
                            formik.errors.ios_file && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {formik.errors.ios_file}
                              </p>
                            )}
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* Android Deployment */}
                  <div
                    className={`p-3 rounded-lg border transition-all ${formik.values.platforms.android.available ? "bg-green-500/[0.03] border-green-500/20" : "bg-white/5 border-white/10"}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${formik.values.platforms.android.available ? "bg-green-500 text-white shadow-lg shadow-green-500/30" : "bg-white/10 text-gray-500"}`}
                        >
                          <DiAndroid size={16} />
                        </div>
                        <h4
                          className={`font-bold text-sm ${formik.values.platforms.android.available ? "text-white" : "text-gray-500"}`}
                        >
                          Android App
                        </h4>
                      </div>
                      <CustomCheckbox
                        checked={formik.values.platforms.android.available}
                        onChange={(val) =>
                          formik.setFieldValue(
                            "platforms.android.available",
                            val,
                          )
                        }
                      />
                    </div>
                    {formik.values.platforms.android.available && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="space-y-2"
                      >
                        <div className="space-y-1">
                          <input
                            type="number"
                            placeholder="Price ($)"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-green-500/50 outline-none"
                            {...formik.getFieldProps("platforms.android.price")}
                          />
                          {formik.touched.platforms?.android?.price &&
                            formik.errors.platforms?.android?.price && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {formik.errors.platforms.android.price}
                              </p>
                            )}
                        </div>
                        <div className="space-y-1">
                          <input
                            type="text"
                            placeholder="Min Android Version"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-green-500/50 outline-none"
                            {...formik.getFieldProps(
                              "platforms.android.system_requirements.android_version",
                            )}
                          />
                          {formik.touched.platforms?.android
                            ?.system_requirements?.android_version &&
                            formik.errors.platforms?.android
                              ?.system_requirements?.android_version && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {
                                  formik.errors.platforms.android
                                    .system_requirements.android_version
                                }
                              </p>
                            )}
                        </div>
                        <div className="space-y-1">
                          <div className="relative h-12 rounded-lg bg-white/5 border-2 border-dashed border-white/10 flex items-center px-3 hover:border-green-500/50 transition-all">
                            <FaPlus size={12} className="text-gray-500 mr-2" />
                            <p className="text-[9px] font-black uppercase text-gray-500 truncate">
                              {formik.values.android_file
                                ? typeof formik.values.android_file === "string"
                                  ? "Current Package: " +
                                    formik.values.android_file.split("/").pop()
                                  : formik.values.android_file.name
                                : "Upload .apk Build"}
                            </p>
                            <input
                              type="file"
                              accept=".apk"
                              className="absolute inset-0 opacity-0 cursor-pointer"
                              onChange={(e) =>
                                formik.setFieldValue(
                                  "android_file",
                                  e.target.files[0],
                                )
                              }
                            />
                          </div>
                          {formik.touched.android_file &&
                            formik.errors.android_file && (
                              <p className="text-red-400 text-[8px] ml-1">
                                {formik.errors.android_file}
                              </p>
                            )}
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 bg-primary/5 p-2 rounded-lg border border-primary/20">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formik.values.isActive}
                  onChange={formik.handleChange}
                  className="w-5 h-5 rounded-md border-white/10 bg-white/5 text-primary focus:ring-primary transition-all cursor-pointer"
                />
                <label
                  htmlFor="isActive"
                  className="text-sm font-bold text-white cursor-pointer select-none"
                >
                  Live Visibility (Make game visible to users immediately)
                </label>
              </div>
            </section>

            <div className="pt-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={handleCreateClose}
                className=" p-2 rounded-lg bg-white/5 border border-white/10 text-white font-bold text-sm uppercase tracking-widest hover:bg-white/10 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className=" p-2 rounded-lg bg-gradient-primary text-white font-bold text-sm uppercase tracking-widest shadow-[0_0_20px_rgba(98,99,237,0.3)] hover:shadow-primary/50 transition-all disabled:opacity-50"
              >
                {loading
                  ? "Transmitting Data..."
                  : gameData
                    ? "Update Game Project"
                    : "Initiate Game Launch"}
              </button>
            </div>
          </form>
        </motion.div>
      </Modal>

      {/* Blog Schedule Modal */}
      <Modal
        open={blogOpen}
        onClose={handleBlogClose}
        className="flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#141414] border border-white/10 rounded-xl p-4 w-full max-w-md shadow-2xl"
        >
          <h2 className="text-2xl font-black text-white mb-2">Auto-Blog</h2>
          <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mb-6">
            AI Content Scheduling
          </p>

          <form onSubmit={handleScheduleBlog} className="space-y-3">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-gray-500 tracking-widest ml-1">
                Blog Title
              </label>
              <input
                type="text"
                value={blogData.title}
                onChange={(e) =>
                  setBlogData({ ...blogData, title: e.target.value })
                }
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white focus:border-primary outline-none"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-gray-500 tracking-widest ml-1">
                Broadcast Time
              </label>
              <input
                type="datetime-local"
                value={blogData.scheduleTime}
                onChange={(e) =>
                  setBlogData({ ...blogData, scheduleTime: e.target.value })
                }
                className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white focus:border-primary outline-none scheme-dark"
                required
              />
            </div>

            <div className="pt-4 flex gap-3">
              <button
                type="button"
                onClick={handleBlogClose}
                className="flex-1 py-4 rounded-2xl border border-white/10 text-white font-bold hover:bg-white/5"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loadingBlog}
                className="flex-[2] py-4 rounded-2xl bg-gradient-primary text-white font-bold shadow-lg shadow-primary/20 transition-all"
              >
                {loadingBlog ? "Scheduling..." : "Schedule Broadcast"}
              </button>
            </div>
          </form>
        </motion.div>
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        open={delOpen}
        onClose={handleDeleteClose}
        className="flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-[#141414] border border-white/10 rounded-xl p-4 text-center w-full max-w-sm shadow-2xl"
        >
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-6 text-2xl border border-red-500/20">
            <RiDeleteBin6Fill />
          </div>
          <h2 className="text-2xl font-bold mb-3 text-white">
            Permanently Delete?
          </h2>
          <p className="text-gray-400 text-sm mb-10 leading-relaxed">
            This action will remove{" "}
            <span className="text-white font-bold">"{gameData?.title}"</span>{" "}
            and all associated assets from the platform. This cannot be undone.
          </p>
          <div className="flex gap-4">
            <button
              onClick={handleDeleteClose}
              className="flex-1 py-4 rounded-2xl bg-white/5 hover:bg-white/10 transition-colors border border-white/10 text-white font-bold"
            >
              Abort
            </button>
            <button
              onClick={handleDeleteGame}
              className="flex-1 py-4 rounded-2xl bg-red-500 text-white font-bold shadow-[0_0_30px_rgba(239,68,68,0.3)]"
            >
              Delete Forever
            </button>
          </div>
        </motion.div>
      </Modal>
    </motion.div>
  );
}
